import * as React from 'react'
import {useEffect, useRef, useState} from 'react'
import {Cpu, Loader2, MessageSquare, Send, X} from 'lucide-react';
import {ChatMessage} from "../../types";
import {BRAND} from "../brand";

const ChatAssistant: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([
        {
            id: 'init',
            role: 'model',
            text: `Greetings. I am NOVA, ${BRAND.name}'s AI consultant. Ask me about our services, equipment, or production capabilities.`,
            timestamp: new Date()
        }
    ]);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isOpen]);

    const handleSend = async () => {
        if (!input.trim()) return;

        const userMsg: ChatMessage = {
            id: Date.now().toString(),
            role: 'user',
            text: input,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setIsLoading(true);

        const responseText = "";

        const modelMsg: ChatMessage = {
            id: (Date.now() + 1).toString(),
            role: 'model',
            text: responseText,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, modelMsg]);
        setIsLoading(false);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSend();
    };

    return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
            {/* Chat Window */}
            {isOpen && (
                <div
                    className="mb-4 w-[350px] md:w-[400px] h-[500px] bg-black/90 backdrop-blur-xl border border-lumina-accent/30 rounded-lg flex flex-col shadow-2xl shadow-lumina-accent/10 overflow-hidden animate-in fade-in slide-in-from-bottom-10 duration-300">
                    {/* Header */}
                    <div className="p-4 border-b border-gray-800 bg-gray-900/50 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <Cpu className="w-5 h-5 text-lumina-accent animate-pulse"/>
                            <div>
                                <h3 className="font-display font-bold text-white text-sm">NOVA AI</h3>
                                <span className="text-[10px] text-lumina-accent uppercase tracking-wider">Online</span>
                            </div>
                        </div>
                        <button onClick={() => setIsOpen(false)}
                                className="text-gray-400 hover:text-white transition-colors">
                            <X className="w-5 h-5"/>
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {messages.map((msg) => (
                            <div
                                key={msg.id}
                                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                                <div
                                    className={`max-w-[80%] p-3 rounded-lg text-sm leading-relaxed ${
                                        msg.role === 'user'
                                            ? 'bg-lumina-accent/20 text-white border border-lumina-accent/20 rounded-tr-none'
                                            : 'bg-gray-800/50 text-gray-200 border border-gray-700 rounded-tl-none'
                                    }`}
                                >
                                    {msg.text}
                                </div>
                            </div>
                        ))}
                        {isLoading && (
                            <div className="flex justify-start">
                                <div
                                    className="bg-gray-800/50 p-3 rounded-lg rounded-tl-none border border-gray-700 flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 text-lumina-accent animate-spin"/>
                                    <span className="text-xs text-gray-400">Processing...</span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef}/>
                    </div>

                    {/* Input */}
                    <div className="p-4 border-t border-gray-800 bg-gray-900/50">
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyPress}
                                placeholder="Ask about gear or services..."
                                className="flex-1 bg-black/50 border border-gray-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-lumina-accent transition-colors"
                            />
                            <button
                                onClick={handleSend}
                                disabled={isLoading || !input.trim()}
                                className="p-2 bg-lumina-accent text-black rounded hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Send className="w-4 h-4"/>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toggle Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="group relative w-14 h-14 bg-lumina-accent/10 border border-lumina-accent rounded-full flex items-center justify-center text-lumina-accent hover:bg-lumina-accent hover:text-black transition-all duration-300 shadow-lg hover:shadow-lumina-accent/50"
            >
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-lumina-purple rounded-full animate-pulse"></span>
                {isOpen ? <X className="w-6 h-6"/> : <MessageSquare className="w-6 h-6"/>}
            </button>
        </div>
    );
};

export default ChatAssistant;
